import React, { lazy, useEffect, useMemo, useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
const VisNetwork = lazy(() => import('./VisNetwork'));
import {
    getDBEngine,
    getLineageType,
    LineageData,
    LineageNode,
    LineageProps,
    NodesInfoObject,
    Orientation,
    RootNode,
} from './index';
import { lineageAPI } from 'api/lineage';
import { getLineageDiagramConfig } from './config';
import { LinageDataProcessor } from './LinageDataProcessor';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { IdType } from 'vis-network/declarations/network/Network';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getIcon } from './Icons';
import useBundleTranslation from 'i18n';
import { AssocArray } from 'tools/types';

const Lineage = ({ showLineage, onHide, id, type }: LineageProps) => {
    const appTheme = useTheme();
    const { t } = useBundleTranslation('components/common/lineage/lineage');

    const popupSettings: PopupSettings = {
        noButtons: true,
        title: t('lineage'),
        customMaxWidth: '100%',
    };

    const dataProcessor = new LinageDataProcessor();

    const [nodesInfo, setNodesInfo] = useState<NodesInfoObject>({});
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<LineageData | null>(null);
    const [processedNodes, setProcessedNodes] = useState<LineageNode[]>([]);
    const [showLegend, setShowLegend] = useState(false);
    const [orientation, setOrientation] = useState<Orientation>('UD');
    const [rawData, setRawData] = useState<LineageData>();
    const [maxTotalNodes, setMaxTotalNodes] = useState<number>(0);
    const [externalIcons, setExternalIcons] = useState<AssocArray<string>>({});
    const [previousNode, setPreviousNode] = useState<RootNode | null>(null);

    const focusThisNode = (nodeId: IdType, nodeType: string) => {
        dataProcessor.clearData();
        setLoading(true);

        lineageAPI.getDiagramConfig(getLineageType(nodeType), nodeId.toString()).then((response) => {
            setLoading(false);
            setRawData(response.data);
        });
    };

    const getStyledNodes = (processedNodes: LineageNode[]) => {
        return processedNodes.map((node) => {
            if (node.params.type === 'root') {
                node.image = {
                    unselected: getIcon(
                        appTheme,
                        node.group,
                        'metric-insights',
                        'plugin',
                        true,
                        Boolean(node.params.certified),
                        false,
                    ),
                    selected: getIcon(
                        appTheme,
                        node.group,
                        'metric-insights',
                        'plugin',
                        true,
                        Boolean(node.params.certified),
                        false,
                        'clicked',
                    ),
                };
                node.font = {
                    color: appTheme.palette.primary.main,
                };

                if (node.params.certified) {
                    node.font = {
                        color: appTheme.palette.success.main,
                    };
                }
            }

            if (node.params.type === 'collapsed') {
                node.image = getIcon(
                    appTheme,
                    node.params.type,
                    'metric-insights',
                    'plugin',
                    false,
                    Boolean(node.params.certified),
                    true,
                );
            }

            if ((node.group === 'plugin' || node.group === 'external-dataset') && node.params.pluginName) {
                node.image = {
                    unselected: getIcon(
                        appTheme,
                        node.group,
                        node.params.pluginName,
                        'plugin',
                        node.params.type === 'root',
                        Boolean(node.params.certified),
                    ),
                    selected: getIcon(
                        appTheme,
                        node.group,
                        node.params.pluginName,
                        'plugin',
                        node.params.type === 'root',
                        Boolean(node.params.certified),
                        false,
                        'clicked',
                    ),
                };
            }

            if (node.group === 'external-metadata' || node.group === 'external-source') {
                if (node.params.pluginName && node.params.pluginName !== 'table') {
                    node.image = {
                        unselected: getIcon(
                            appTheme,
                            'plugin',
                            node.params.pluginName,
                            'plugin',
                            node.params.type === 'root',
                            Boolean(node.params.certified),
                        ),
                        selected: getIcon(
                            appTheme,
                            'plugin',
                            node.params.pluginName,
                            'plugin',
                            node.params.type === 'root',
                            Boolean(node.params.certified),
                            false,
                            'clicked',
                        ),
                    };
                } else {
                    const dbEngine = getDBEngine(node);

                    node.image = {
                        unselected: getIcon(
                            appTheme,
                            node.group,
                            dbEngine,
                            'db',
                            node.params.type === 'root',
                            Boolean(node.params.certified),
                        ),
                        selected: getIcon(
                            appTheme,
                            node.group,
                            dbEngine,
                            'db',
                            node.params.type === 'root',
                            Boolean(node.params.certified),
                            false,
                            'clicked',
                        ),
                    };
                }
            }

            if (node.group === 'source') {
                const dbEngine = getDBEngine(node);

                node.image = {
                    unselected: getIcon(appTheme, 'sql-data-source', dbEngine, 'db', node.params.type === 'root'),
                    selected: getIcon(
                        appTheme,
                        'sql-data-source',
                        dbEngine,
                        'db',
                        node.params.type === 'root',
                        false,
                        false,
                        'clicked',
                    ),
                };
            }

            if (node.label && node.label.length > 45) {
                node.label = node.label.substring(0, 42) + '...';
            }

            return node;
        });
    };

    useEffect(() => {
        if (rawData) {
            dataProcessor.prepareData(rawData);
            const { processedNodes, filteredEdges, nodesInfo } = dataProcessor.getData();
            const processedStyledNodes = getStyledNodes(processedNodes);
            setProcessedNodes(processedStyledNodes);
            setData({ nodes: processedStyledNodes, edges: filteredEdges });
            setNodesInfo(nodesInfo);
        }

        setLoading(false);
    }, [rawData]);

    useEffect(() => {
        setData(null);

        if (showLineage) {
            setLoading(true);

            lineageAPI
                .drawLineageDiagram(getLineageType(type), id)
                .then((response) => {
                    setOrientation(response.data.lineageDiagramConfig.direction);
                    setShowLegend(response.data.lineageDiagramConfig.showSetting);
                    setRawData(response.data.lineageDiagramData);
                    setMaxTotalNodes(response.data.maxTotalNodes);
                    setExternalIcons(response.data.externalIcons);
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    }, [showLineage]);

    const options = getLineageDiagramConfig(appTheme);

    const onlyOneNodePresent = useMemo(() => {
        return processedNodes.length === 1;
    }, [processedNodes.length]);

    const saveConfig = (newOrientation: Orientation, newShowLegend: boolean) => {
        lineageAPI.saveDiagramConfig({
            direction: newOrientation,
            showSetting: newShowLegend,
        });
    };

    const onOrientationChange = (newOrientation: Orientation) => {
        setOrientation(newOrientation);
        saveConfig(newOrientation, showLegend);
    };

    const onShowLegendChange = (newShowLegend: boolean) => {
        setShowLegend(newShowLegend);
        saveConfig(orientation, newShowLegend);
    };

    return showLineage ? (
        <Popup
            settings={popupSettings}
            open={showLineage}
            onHide={onHide}
            scrollType={'paper'}
            className={'linagePopup'}
            sx={{
                '&.linagePopup .MuiDialog-paperScrollPaper': {
                    height: '100%',
                    minHeight: '350px',
                },
                '&.linagePopup .MuiDialogContent-root': {
                    overflowY: 'auto',
                },
            }}
        >
            {loading && <LoadingPlaceholder />}
            {!loading && data && !onlyOneNodePresent && (
                <Box sx={{ py: 1, height: '100%' }}>
                    <VisNetwork
                        data={data}
                        options={options}
                        nodesInfo={nodesInfo}
                        initNodes={processedNodes}
                        showLegend={showLegend}
                        orientation={orientation}
                        setShowLegend={onShowLegendChange}
                        setOrientation={onOrientationChange}
                        focusThisNode={focusThisNode}
                        maxTotalNodes={maxTotalNodes}
                        externalIcons={externalIcons}
                        previousNode={previousNode}
                        setPreviousNode={setPreviousNode}
                    />
                </Box>
            )}
            {!loading && data && onlyOneNodePresent && (
                <Box sx={{ height: 600 }}>
                    <Typography>{t('not_available')}</Typography>
                </Box>
            )}
        </Popup>
    ) : null;
};

export default Lineage;
